import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, type AdminInteraction } from '../api/client.js';
import { useAdminClient } from './useAdminClient.js';

/**
 * Poll the interaction queue every 2s while visible and live, using `updatedAfter` cursor semantics
 * (§24.4). Pauses when the tab is hidden, backs off to 5/10/30s on repeated failures, and resumes
 * immediately on visibility or connectivity restoration. Multiple performer devices converge through
 * server state — this hook only reads. No WebSocket is used (§24.4).
 */

const BASE_INTERVAL_MS = 2_000;
const BACKOFF_MS = [5_000, 10_000, 30_000] as const;

export type Connectivity = 'online' | 'reconnecting' | 'offline';

export interface QueueState {
  /** All interactions seen so far, newest first. Cursor polling delivers only changed rows. */
  items: AdminInteraction[];
  connectivity: Connectivity;
  /** Epoch ms of the last successful poll — drives the freshness indicator. */
  lastUpdatedAt: number | null;
  error: ApiError | null;
  /** Force an immediate poll (e.g. after a local mutation). */
  refresh: () => void;
  /** Merge a locally-updated interaction so optimistic UI stays consistent between polls. */
  applyLocal: (interaction: AdminInteraction) => void;
}

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible';
}

function sortNewestFirst(a: AdminInteraction, b: AdminInteraction): number {
  return b.submittedAt.localeCompare(a.submittedAt);
}

export function useInteractionQueue(gigId: string, active: boolean): QueueState {
  const client = useAdminClient();
  const [items, setItems] = useState<AdminInteraction[]>([]);
  const [connectivity, setConnectivity] = useState<Connectivity>('online');
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  // Refs so the polling loop reads live values without re-subscribing.
  const cursorRef = useRef<string | undefined>(undefined);
  const byIdRef = useRef<Map<string, AdminInteraction>>(new Map());
  const failuresRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  const publish = useCallback(() => {
    setItems([...byIdRef.current.values()].sort(sortNewestFirst));
  }, []);

  const applyLocal = useCallback(
    (interaction: AdminInteraction) => {
      byIdRef.current.set(interaction.interactionId, interaction);
      publish();
    },
    [publish],
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    if (!activeRef.current || !isVisible() || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      // Not eligible to poll right now; the visibility/online listeners will resume us.
      if (typeof navigator !== 'undefined' && !navigator.onLine) setConnectivity('offline');
      return;
    }
    try {
      const page = await client.listInteractions(gigId, cursorRef.current);
      if (cancelledRef.current) return;
      for (const it of page.interactions) byIdRef.current.set(it.interactionId, it);
      if (page.updatedAfter) cursorRef.current = page.updatedAfter;
      publish();
      failuresRef.current = 0;
      setError(null);
      setConnectivity('online');
      setLastUpdatedAt(Date.now());
    } catch (err) {
      if (cancelledRef.current) return;
      failuresRef.current += 1;
      setConnectivity('reconnecting');
      if (err instanceof ApiError) setError(err);
    } finally {
      if (!cancelledRef.current) {
        // Schedule the next poll: 2s while healthy, else 5/10/30s backoff (§24.4).
        clearTimer();
        const f = failuresRef.current;
        const delay =
          f === 0 ? BASE_INTERVAL_MS : BACKOFF_MS[Math.min(f - 1, BACKOFF_MS.length - 1)]!;
        timerRef.current = setTimeout(() => void poll(), delay);
      }
    }
  }, [client, gigId, publish, clearTimer]);

  const refresh = useCallback(() => {
    failuresRef.current = 0;
    clearTimer();
    void poll();
  }, [clearTimer, poll]);

  // Reset cursor + cache when the gig changes.
  useEffect(() => {
    cursorRef.current = undefined;
    byIdRef.current = new Map();
    failuresRef.current = 0;
    setItems([]);
    setLastUpdatedAt(null);
  }, [gigId]);

  useEffect(() => {
    cancelledRef.current = false;
    if (active) void poll();
    else clearTimer();

    const onVisibility = () => {
      if (isVisible() && activeRef.current) refresh();
      else clearTimer();
    };
    const onOnline = () => {
      setConnectivity('online');
      if (activeRef.current) refresh();
    };
    const onOffline = () => {
      setConnectivity('offline');
      clearTimer();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      cancelledRef.current = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [gigId, active]);

  return { items, connectivity, lastUpdatedAt, error, refresh, applyLocal };
}
